import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES, USER_ROLES } from '../../constants';

const Sidebar = ({
  isOpen = false,
  onClose,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasRoleOrAbove } = useAuth();
  const [expandedMenus, setExpandedMenus] = React.useState({});

  // Toggle submenu
  const toggleSubmenu = menuName => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName],
    }));
  };

  // Navigation items configuration
  const navigationItems = [
    {
      name: 'Dashboard',
      path: ROUTES.DASHBOARD,
      icon: (
        <svg
          className='w-5 h-5'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z'
          />
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z'
          />
        </svg>
      ),
    },
    {
      name: 'Creators',
      path: ROUTES.MARKETERS,
      minRole: USER_ROLES.ADMIN,
      icon: (
        <svg
          className='w-5 h-5'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
          />
        </svg>
      ),
    },
    {
      name: 'Merchants',
      path: ROUTES.MERCHANTS,
      minRole: USER_ROLES.ADMIN,
      icon: (
        <svg
          className='w-5 h-5'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
          />
        </svg>
      ),
    },
    {
      name: 'Shoppers',
      minRole: USER_ROLES.ADMIN,
      icon: (
        <svg
          className='w-5 h-5'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z'
          />
        </svg>
      ),
      hasSubmenu: true,
      submenu: [
        {
          name: 'Shoppers Management',
          path: ROUTES.SHOPPERS,
          minRole: USER_ROLES.ADMIN,
        },
        {
          name: 'Shoppers Payout',
          path: ROUTES.SHOPPER_PAYOUT,
          minRole: USER_ROLES.ADMIN,
        },
      ],
    },
    {
      name: 'Topup Balance',
      path: ROUTES.TOPUP_CASHBACK,
      minRole: USER_ROLES.ADMIN,
      icon: (
        <svg
          className='w-5 h-5'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
          />
        </svg>
      ),
    },
    {
      name: 'Transactions',
      minRole: USER_ROLES.ADMIN,
      icon: (
        <svg
          className='w-5 h-5'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
          />
        </svg>
      ),
      hasSubmenu: true,
      submenu: [
        {
          name: '1 Snap',
          path: ROUTES.ONE_SNAP,
          minRole: USER_ROLES.ADMIN,
        },
        {
          name: 'Reversal',
          path: ROUTES.REVERSAL,
          minRole: USER_ROLES.ADMIN,
        },
      ],
    },
    {
      name: 'Purchases',
      path: ROUTES.PURCHASES,
      minRole: USER_ROLES.ADMIN,
      icon: (
        <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 7h18M3 11h5m-5 6V5a2 2 0 012-2h14a2 2 0 012 2v8M3 17h8m5-3l-3 3 3 3m-3-3h8m-3 1l3 3-3 3' />
        </svg>
      ),
    },
    {
      name: 'Master',
      icon: (
        <svg
          className='w-5 h-5'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
          />
        </svg>
      ),
      hasSubmenu: true,
      submenu: [
        {
          name: 'Users',
          path: ROUTES.USERS,
        },
        {
          name: 'Assets',
          path: ROUTES.MASTER_ASSETS,
        },
        {
          name: 'SSO & API Keys',
          path: ROUTES.OAUTH_MANAGEMENT,
        },
      ],
    },
    // {
    //   name: 'Roles',
    //   path: ROUTES.ROLES,
    //   icon: (
    //     <svg
    //       className='w-5 h-5'
    //       fill='none'
    //       stroke='currentColor'
    //       viewBox='0 0 24 24'
    //     >
    //       <path
    //         strokeLinecap='round'
    //         strokeLinejoin='round'
    //         strokeWidth={2}
    //         d='M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z'
    //       />
    //     </svg>
    //   ),
    // },
    // {
    //   name: 'Profile',
    //   path: ROUTES.PROFILE,
    //   icon: (
    //     <svg
    //       className='w-5 h-5'
    //       fill='none'
    //       stroke='currentColor'
    //       viewBox='0 0 24 24'
    //     >
    //       <path
    //         strokeLinecap='round'
    //         strokeLinejoin='round'
    //         strokeWidth={2}
    //         d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
    //       />
    //     </svg>
    //   ),
    // },
  ];

  const isActiveRoute = path => {
    // Handle root route specifically
    if (path === ROUTES.DASHBOARD) {
      return location.pathname === path || location.pathname === '/';
    }
    return (
      location.pathname === path || location.pathname.startsWith(path + '/')
    );
  };

  // Keep the submenu that owns the current route open on navigation.
  React.useEffect(() => {
    const openMenus = {};
    navigationItems.forEach(item => {
      if (!item.hasSubmenu || !item.submenu) return;
      if (item.submenu.some(subItem => isActiveRoute(subItem.path))) {
        openMenus[item.name] = true;
      }
    });
    if (Object.keys(openMenus).length > 0) {
      setExpandedMenus(prev => ({ ...prev, ...openMenus }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isMenuActive = item => {
    if (item.path) {
      return isActiveRoute(item.path);
    }
    if (item.hasSubmenu && item.submenu) {
      return item.submenu.some(subItem => isActiveRoute(subItem.path));
    }
    return false;
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className='fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden'
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-gray-200 
          transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-64'}
          lg:translate-x-0 lg:static lg:inset-y-0 flex-shrink-0
        `}
      >
        {/* Sidebar content */}
        <div className='flex flex-col flex-1 min-h-0'>
          {/* Logo and Collapse Button */}
          <div className='flex items-center justify-between h-16 px-4 bg-white border-b border-gray-200 flex-shrink-0'>
            <Link
              to={ROUTES.DASHBOARD}
              className='flex items-center'
              title='SKAI1 Admin Panel'
            >
              <div className='w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center'>
                <span className='text-white font-bold text-lg'>S</span>
              </div>
              {!isCollapsed && (
                <span className='ml-2 text-xl font-bold text-gray-900'>
                  SKAI1
                </span>
              )}
            </Link>
            <button
              onClick={onToggleCollapse}
              className='p-1.5 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100  hidden lg:block'
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg
                className={`w-5 h-5 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''
                  }`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className='flex-1 px-2 py-4 space-y-1 bg-white overflow-y-auto'>
            {navigationItems
              .filter(item => !item.minRole || hasRoleOrAbove(item.minRole))
              .map(item => {
                if (item.hasSubmenu && item.submenu) {
                  return {
                    ...item,
                    submenu: item.submenu.filter(
                      sub => !sub.minRole || hasRoleOrAbove(sub.minRole)
                    ),
                  };
                }
                return item;
              })
              .filter(item => !item.hasSubmenu || (item.submenu && item.submenu.length > 0))
              .map(item => {
                const isActive = isMenuActive(item);
                const isExpanded =
                  expandedMenus[item.name] !== undefined
                    ? expandedMenus[item.name]
                    : isActive;

                // Menu with submenu
                if (item.hasSubmenu) {
                  // Collapsed there is no room for the submenu, so the icon has
                  // to navigate instead of just toggling — otherwise the whole
                  // section is unreachable from the nav.
                  const collapsedTarget = item.submenu?.[0]?.path;
                  const handleParentClick = () => {
                    if (isCollapsed && collapsedTarget) {
                      onClose?.();
                      navigate(collapsedTarget);
                      return;
                    }
                    toggleSubmenu(item.name);
                  };

                  return (
                    <div key={item.name}>
                      <button
                        onClick={handleParentClick}
                        className={`
                        group flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150
                        ${isActive
                            ? 'bg-blue-50 border-l-4 border-blue-600 text-blue-700'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                          }
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                      `}
                        title={isCollapsed ? item.name : ''}
                      >
                        <div
                          className={`
                          flex-shrink-0 mr-3 transition-colors duration-150
                          ${isActive
                              ? 'text-blue-600'
                              : 'text-gray-400 group-hover:text-gray-500'
                            }
                        `}
                        >
                          {item.icon}
                        </div>
                        {!isCollapsed && (
                          <>
                            <span className='truncate flex-1 text-left'>
                              {item.name}
                            </span>
                            <svg
                              className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                                }`}
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M19 9l-7 7-7-7'
                              />
                            </svg>
                          </>
                        )}
                      </button>

                      {/* Submenu items */}
                      {!isCollapsed && isExpanded && item.submenu && (
                        <div className='mt-1 space-y-1'>
                          {item.submenu.map(subItem => {
                            const isSubActive = isActiveRoute(subItem.path);
                            return (
                              <Link
                                key={subItem.name}
                                to={subItem.path}
                                onClick={onClose}
                                className={`
                                group flex items-center pl-11 pr-3 py-2 text-sm font-medium rounded-md transition-colors duration-150
                                ${isSubActive
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                  }
                                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                              `}
                              >
                                <div className='flex items-center'>
                                  <div
                                    className={`w-1.5 h-1.5 rounded-full mr-3 ${isSubActive ? 'bg-blue-600' : 'bg-gray-400'
                                      }`}
                                  ></div>
                                  <span className='truncate'>{subItem.name}</span>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                // Regular menu item without submenu
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={onClose}
                    className={`
                    group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150
                    ${isActive
                        ? 'bg-blue-50 border-l-4 border-blue-600 text-blue-700'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                      }
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  `}
                    title={isCollapsed ? item.name : ''}
                  >
                    <div
                      className={`
                      flex-shrink-0 mr-3 transition-colors duration-150
                      ${isActive
                          ? 'text-blue-600'
                          : 'text-gray-400 group-hover:text-gray-500'
                        }
                    `}
                    >
                      {item.icon}
                    </div>
                    {!isCollapsed && (
                      <span className='truncate'>{item.name}</span>
                    )}
                    {isActive && !isCollapsed && (
                      <div className='ml-auto'>
                        <div className='w-1.5 h-1.5 bg-blue-600 rounded-full'></div>
                      </div>
                    )}
                  </Link>
                );
              })}
          </nav>

          {/* Bottom section */}
          {!isCollapsed && (
            <div className='flex-shrink-0 p-4 border-t border-gray-200'>
              <div className='text-xs text-gray-500 text-center'>
                <p>SKAI1 Admin v1.0</p>
                <p className='mt-1'>© {new Date().getFullYear()} SKAI1</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
